import { ref, watch, computed } from 'vue';
import type { ExtendedMenuItem, MenuItemVariant, MenuItemModifierGroupLink } from '@/types/order';

interface UseItemOptionsProps {
  item: ExtendedMenuItem;
  getVariantPrice: (variant: MenuItemVariant) => number;
}

export function useItemOptions(props: UseItemOptionsProps) {
  // Internal state
  const selectedVariant = ref<MenuItemVariant | null>(null);
  const notes = ref('');
  const quantity = ref(1);
  const selectedModifierOptions = ref<Record<number, number[]>>({});
  const openAccordions = ref<Set<number>>(new Set());
  const selectedItemType = ref<'dine_in' | 'takeaway'>('dine_in');

  // Track current item ID to avoid unnecessary re-renders
  const currentItemId = ref<number | null>(null);

  // Reset when item changes
  watch(() => props.item?.id, (newItemId, oldItemId) => {
    if (newItemId !== oldItemId && props.item) {
      currentItemId.value = newItemId;
      
      // Auto-select first variant if item has variants and no base price
      if (props.item.variants && props.item.variants.length > 0 && (!props.item.price || props.item.price === 0)) {
        selectedVariant.value = props.item.variants[0];
      } else {
        selectedVariant.value = null;
      }
      notes.value = '';
      selectedModifierOptions.value = {};
      
      // Open first accordion by default, others closed
      openAccordions.value = new Set();
      if (props.item.modifier_groups && props.item.modifier_groups.length > 0) {
        openAccordions.value.add(props.item.modifier_groups[0].group_id);
      }

      (props.item.modifier_groups || []).forEach((groupLink) => {
        // No auto-selection by default - users must manually select all options
        // Required groups will be validated when user tries to add item
      });

      quantity.value = 1;
      selectedItemType.value = 'dine_in'; // Reset to default
    }
  }, { immediate: true });

  const modifierDelta = computed(() => {
    if (!props.item?.modifier_groups?.length) return 0;

    return props.item.modifier_groups.reduce((sum, groupLink) => {
      const selectedIds = selectedModifierOptions.value[groupLink.group_id] || [];
      const groupDelta = (groupLink.group.options || [])
        .filter((option) => selectedIds.includes(option.id))
        .reduce((acc, option) => acc + (option.price_delta || 0), 0);
      return sum + groupDelta;
    }, 0);
  });

  // Computed total price
  const totalPrice = computed(() => {
    if (!props.item) return 0;
    
    let price = 0;
    if (selectedVariant.value) {
      price = props.getVariantPrice(selectedVariant.value);
    } else {
      // Use discount price if available, otherwise regular price
      price = (props.item.discount_price && props.item.discount_price > 0) 
        ? props.item.discount_price 
        : (props.item.price || 0);
    }
    
    return (price + modifierDelta.value) * quantity.value;
  });

  const isGroupSelectionValid = (groupLink: MenuItemModifierGroupLink): boolean => {
    const selectedCount = (selectedModifierOptions.value[groupLink.group_id] || []).length;
    const minRequired = groupLink.required ? groupLink.min_select : 0;
    return selectedCount >= minRequired && selectedCount <= groupLink.max_select;
  };

  const isModifierSelectionValid = computed(() => {
    if (!props.item?.modifier_groups?.length) return true;
    return props.item.modifier_groups.every((groupLink) => isGroupSelectionValid(groupLink));
  });

  const isOptionSelected = (groupId: number, optionId: number): boolean => {
    return (selectedModifierOptions.value[groupId] || []).includes(optionId);
  };

  const isAccordionOpen = (groupId: number): boolean => {
    return openAccordions.value.has(groupId);
  };

  const toggleAccordion = (groupId: number) => {
    if (openAccordions.value.has(groupId)) {
      openAccordions.value.delete(groupId);
    } else {
      openAccordions.value.add(groupId);
    }
  };

  const toggleModifierOption = (groupId: number, optionId: number, maxSelect: number) => {
    const selectedIds = [...(selectedModifierOptions.value[groupId] || [])];
    const alreadySelected = selectedIds.includes(optionId);

    if (alreadySelected) {
      selectedModifierOptions.value[groupId] = selectedIds.filter((id) => id !== optionId);
      return;
    }

    if (maxSelect <= 1) {
      selectedModifierOptions.value[groupId] = [optionId];
      return;
    }

    if (selectedIds.length >= maxSelect) {
      return;
    }

    selectedModifierOptions.value[groupId] = [...selectedIds, optionId];
  };

  const decreaseQuantity = () => {
    if (quantity.value > 1) {
      quantity.value--;
    }
  };

  const increaseQuantity = () => {
    quantity.value++;
  };

  const buildModifiersSummary = (): string => {
    if (!props.item?.modifier_groups?.length) return '';

    const parts = props.item.modifier_groups
      .map((groupLink) => {
        const selectedIds = selectedModifierOptions.value[groupLink.group_id] || [];
        if (selectedIds.length === 0) return '';

        const names = groupLink.group.options
          .filter((opt) => selectedIds.includes(opt.id))
          .map((opt) => opt.name)
          .join(', ');

        return `${names}`;
      })
      .filter((part) => part.length > 0);

    return parts.join(' | ');
  };

  const reset = () => {
    selectedVariant.value = null;
    notes.value = '';
    selectedModifierOptions.value = {};
    quantity.value = 1;
    selectedItemType.value = 'dine_in'; // Reset to default
    openAccordions.value.clear();
  };

  return {
    // State
    selectedVariant,
    notes,
    quantity,
    selectedModifierOptions,
    openAccordions,
    selectedItemType,
    currentItemId,
    
    // Computed
    modifierDelta,
    totalPrice,
    isModifierSelectionValid,
    
    // Methods
    isGroupSelectionValid,
    isOptionSelected,
    isAccordionOpen,
    toggleAccordion,
    toggleModifierOption,
    decreaseQuantity,
    increaseQuantity,
    buildModifiersSummary,
    reset,
  };
}
